// Styles de base des modales (overlay, en-tête, pied) partagés avec l'ancienne modale
import '../../../pages/TruckAssignments/manual-assignment-modal.css';

import {
  faDownload,
  faFileExcel,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import useImportOrders from '../../../api/orders-pool/useImportOrders';

const STATUS_LABELS = {
  OK: 'Prête',
  IGNORED: 'BL déjà reçu : ignoré',
};

/**
 * Ce que l'aperçu annonce : lignes lues, BL à créer, BL déjà reçus (jamais
 * modifiés par un import) et lignes en erreur. Une ligne du fichier est un
 * article : un BL de trois articles occupe trois lignes.
 *
 * @param {Array<object>} rows - Les lignes de l'aperçu
 * @returns {object} - { total, toCreate, ignored, error }
 */
export function countPreviewRows(rows) {
  const blKeys = (status) =>
    new Set(
      rows
        .filter((row) => row.status === status && row.delivery_detail_key)
        .map((row) => row.delivery_detail_key)
    ).size;

  return {
    total: rows.length,
    toCreate: blKeys('OK'),
    ignored: blKeys('IGNORED'),
    error: rows.filter((row) => row.status === 'ERROR').length,
  };
}

/**
 * Modale « Importer Excel » du puits de commandes (parcours de tms-chargeur) :
 * lien vers le modèle, dépôt du fichier, aperçu ligne par ligne sans rien
 * créer, puis confirmation. Les lignes en erreur ne bloquent pas la
 * confirmation : les BL corrects sont créés, les autres sont à reprendre dans
 * un second fichier.
 *
 * @param {object} props - Les propriétés
 * @param {boolean} props.isOpen - Ouverture
 * @param {Function} props.onClose - Fermeture
 * @param {Function} props.onImported - Appelé après une confirmation réussie
 * @returns {JSX.Element|null} - La modale
 */
export default function ImportOrdersModal({ isOpen, onClose, onImported }) {
  const {
    preview,
    isUploading,
    isConfirming,
    uploadFile,
    confirmImport,
    reset,
    downloadTemplate,
  } = useImportOrders();

  const [fileName, setFileName] = useState('');
  const inputRef = useRef(null);

  // Chaque ouverture repart d'un écran vide : un aperçu d'hier n'a plus cours
  useEffect(() => {
    if (!isOpen) return;
    reset();
    setFileName('');
    if (inputRef.current) inputRef.current.value = '';
  }, [isOpen, reset]);

  const counts = useMemo(
    () => countPreviewRows(preview?.rows || []),
    [preview]
  );

  if (!isOpen) return null;

  const isBusy = isUploading || isConfirming;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // L'aperçu précédent tombe dès le dépôt : il ne décrit plus le fichier annoncé
    reset();
    setFileName(file.name);
    await uploadFile(file);
  };

  const handleConfirm = async () => {
    const result = await confirmImport(preview.intake_id);
    if (!result) return;
    onImported?.();
    onClose();
  };

  const close = () => {
    if (!isConfirming) onClose();
  };

  return (
    <div className="modal-overlay" onClick={close}>
      <div
        className="modal-container op-import-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Importer des commandes</h2>
            <p className="modal-subtitle">
              Une ligne du fichier = un article d'un BL. Un BL déjà reçu n'est
              jamais modifié par un import : il est ignoré.
            </p>
          </div>
          <button
            className="modal-close-btn"
            onClick={close}
            disabled={isConfirming}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <div className="op-import-modal__actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={downloadTemplate}
            >
              <FontAwesomeIcon icon={faDownload} /> Télécharger le modèle
            </button>
            <label
              className={`btn-primary op-import-modal__file ${
                isBusy ? 'op-import-modal__file--disabled' : ''
              }`}
            >
              <FontAwesomeIcon icon={faFileExcel} />{' '}
              {isUploading ? 'Lecture du fichier…' : 'Choisir un fichier'}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                disabled={isBusy}
                onChange={handleFile}
                hidden
              />
            </label>
            {fileName && (
              <span className="op-import-modal__filename" title={fileName}>
                {fileName}
              </span>
            )}
          </div>

          {preview && (
            <>
              <div className="op-import-modal__summary">
                <span>
                  {counts.total} ligne{counts.total > 1 ? 's' : ''} lue
                  {counts.total > 1 ? 's' : ''}
                </span>
                <span className="op-import-modal__count--ok">
                  {counts.toCreate} BL à créer
                </span>
                {counts.ignored > 0 && (
                  <span>{counts.ignored} BL déjà reçu(s)</span>
                )}
                {counts.error > 0 && (
                  <span className="op-import-modal__count--error">
                    {counts.error} ligne{counts.error > 1 ? 's' : ''} en erreur
                  </span>
                )}
                <span className="op-import-modal__hint">
                  Aucun BL n'est créé tant que vous n'avez pas confirmé.
                </span>
              </div>

              <div className="op-import-modal__table">
                <table>
                  <thead>
                    <tr>
                      <th>Ligne</th>
                      <th>N° de BL</th>
                      <th>État</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr
                        key={row.row_number}
                        className={`op-import-modal__row--${row.status.toLowerCase()}`}
                      >
                        <td>{row.row_number}</td>
                        <td>{row.delivery_detail_key || '—'}</td>
                        <td>
                          {row.status === 'ERROR'
                            ? // Toutes les erreurs de la ligne : on corrige un fichier en une fois
                              `⚠ ${row.errors.join(' · ')}`
                            : [
                                STATUS_LABELS[row.status],
                                // L'avertissement d'un BL déjà reçu redit son statut
                                ...(row.status === 'IGNORED'
                                  ? []
                                  : row.warnings || []),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={close}
            disabled={isConfirming}
          >
            Annuler
          </button>
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={!preview || counts.toCreate === 0 || isBusy}
            title={
              preview && counts.toCreate === 0
                ? 'Aucun BL de ce fichier ne peut être créé'
                : undefined
            }
          >
            {isConfirming
              ? 'Création en cours…'
              : `Créer ${counts.toCreate} BL`}
          </button>
        </div>
      </div>
    </div>
  );
}
